import { userDateFormat } from '@cumu/shared';
import type {
  ClaimBatchPreviewBillingEffect,
  ClaimBatchPreviewIssueGridRow,
  ClaimBatchPreviewReviewLevel
} from '@cumu/shared';
import { controller } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';
import {
  ClientSideGridElement,
  ClientSideGridOptions
} from './client-side-grid-element';

const billingEffectLabels: Record<ClaimBatchPreviewBillingEffect, string> = {
  none: 'No billing impact',
  affects_amount: 'Amount affected',
  exclude_source: "Source won't bill",
  exclude_claim: "Claim won't bill",
  block_batch: 'Blocks batch'
};

const reviewLevelLabels: Record<ClaimBatchPreviewReviewLevel, string> = {
  info: 'Info',
  attention: 'Attention',
  action: 'Action'
};

const reviewLevelClasses: Record<ClaimBatchPreviewReviewLevel, string> = {
  info: 'Label',
  attention: 'Label Label--attention',
  action: 'Label Label--danger'
};

const autoHeightRowLimit = 40;

@controller('claim-batch-preview-issues')
class ClaimBatchPreviewIssuesElement extends ClientSideGridElement<ClaimBatchPreviewIssueGridRow> {
  protected getGridOptions(
    rowData: ClaimBatchPreviewIssueGridRow[]
  ): ClientSideGridOptions<ClaimBatchPreviewIssueGridRow> {
    const useAutoHeight = rowData.length <= autoHeightRowLimit;
    this.gridHost.style.height = useAutoHeight ? '' : '75vh';

    const columnDefs: AgGrid.ColDef<ClaimBatchPreviewIssueGridRow>[] = [
      {
        field: 'review_level',
        headerName: 'Review',
        valueFormatter: ({ value }) =>
          isReviewLevel(value) ? reviewLevelLabels[value] : value,
        cellRenderer: ({ value }: AgGrid.ICellRendererParams) => {
          if (!isReviewLevel(value)) {
            return value ?? '';
          }
          const label = document.createElement('span');
          label.className = reviewLevelClasses[value];
          label.textContent = reviewLevelLabels[value];
          return label;
        },
        width: 125
      },
      {
        field: 'billing_effect',
        headerName: 'Billing effect',
        valueFormatter: ({ value }) =>
          isBillingEffect(value) ? billingEffectLabels[value] : value,
        width: 170
      },
      {
        field: 'message',
        headerName: 'Message',
        wrapText: true,
        autoHeight: true,
        flex: 2,
        minWidth: 280,
        tooltipField: 'message'
      },
      {
        field: 'code',
        headerName: 'Code',
        width: 230
      },
      {
        field: 'activity_id',
        headerName: 'Activity',
        cellRenderer: ({
          data,
          value
        }: AgGrid.ICellRendererParams<ClaimBatchPreviewIssueGridRow>) => {
          if (!value || !data?.activity_href) {
            return '';
          }
          const link = document.createElement('a');
          link.className = 'Link--primary';
          link.href = data.activity_href;
          link.textContent = `${data.activity_definition_title} - ${userDateFormat.format(new Date(data.activity_start_date!))} - ${data.person_full_name}`;
          return link;
        },
        width: 290
      }
    ];

    return {
      getRowId: ({ data }) => data.row_id,
      columnDefs,
      defaultColDef: {
        minWidth: 120
      },
      domLayout: useAutoHeight ? 'autoHeight' : 'normal'
    };
  }
}

function isBillingEffect(
  value: unknown
): value is ClaimBatchPreviewBillingEffect {
  return (
    value === 'none' ||
    value === 'affects_amount' ||
    value === 'exclude_source' ||
    value === 'exclude_claim' ||
    value === 'block_batch'
  );
}

function isReviewLevel(value: unknown): value is ClaimBatchPreviewReviewLevel {
  return value === 'info' || value === 'attention' || value === 'action';
}

declare global {
  interface Window {
    ClaimBatchPreviewIssuesElement: typeof ClaimBatchPreviewIssuesElement;
  }
  interface HTMLElementTagNameMap {
    'claim-batch-preview-issues': ClaimBatchPreviewIssuesElement;
  }
}
